import { Component, computed, DestroyRef, effect, inject, OnInit, signal } from '@angular/core';
import { PageWrapperComponent } from '../../../shared/pages/page-wrapper/page-wrapper.component';
import { Card } from 'primeng/card';
import {
  DateRangePickerComponent,
  DateRangeSelection,
} from '../../../shared/components/date-range-picker/date-range-picker.component';
import { DateTimeUtils } from '../../../shared/utils/date-time-utils';
import { ActivatedRoute, Router } from '@angular/router';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { NgIf } from '@angular/common';
import { ProgressSpinner } from 'primeng/progressspinner';
import { WorkItemsFacadeService } from '../../work-items/services/work-items-facade.service';
import {
  ClientDto,
  DetailedReportResponseDto,
  ReportRequestDto,
  ReportsService,
  SummarizeReportResponseDto,
  WorkItemDto,
  WorkspacesService, WorkspaceUserDto,
} from '../../../api';
import { debounceTime, filter, tap } from 'rxjs';
import { AuthStore } from '../../../auth/services/auth.store';
import { ClientFacadeService } from '../../clients/services/client-facade.service';
import { Button } from 'primeng/button';
import { MultiSelect, MultiSelectChangeEvent } from 'primeng/multiselect';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { FloatLabel } from 'primeng/floatlabel';
import { SelectButton } from 'primeng/selectbutton';
import { SummaryReportComponent } from '../components/summary-report/summary-report.component';
import { DetailedReportComponent } from '../components/detailed-report/detailed-report.component';
import {Panel} from 'primeng/panel';
import {PrimeTemplate} from 'primeng/api';

export const atLeastOneSelectedValidator: ValidatorFn = (
  control: AbstractControl,
): ValidationErrors | null => {
  const clients = control.get('clients')?.value as number[] | null;
  const workItems = control.get('workItems')?.value as number[] | null;
  const billable = control.get('billable')?.value as boolean[] | null;
  const users = control.get('users')?.value as boolean[] | null;

  const hasClients = Array.isArray(clients) && clients.length > 0;
  const hasWorkItems = Array.isArray(workItems) && workItems.length > 0;
  const hasBillable = Array.isArray(billable) && billable.length > 0;
  const hasUsers = Array.isArray(users) && users.length > 0;

  return hasClients || hasWorkItems || hasBillable  || hasUsers ? null : { atLeastOneRequired: true };
};

type ReportView = 'summary' | 'detailed';

interface DateRange {
  start: Date;
  end: Date;
}

@Component({
  selector: 'app-reports-page',
  imports: [
    PageWrapperComponent,
    Card,
    DateRangePickerComponent,
    NgIf,
    ProgressSpinner,
    Button,
    MultiSelect,
    ReactiveFormsModule,
    FormsModule,
    FloatLabel,
    SelectButton,
    SummaryReportComponent,
    DetailedReportComponent,
    Panel,
    PrimeTemplate,
  ],
  templateUrl: './reports-page.component.html',
  styleUrl: './reports-page.component.css',
})
export class ReportsPageComponent implements OnInit {
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly destroyRef = inject(DestroyRef);
  private readonly authStore = inject(AuthStore);
  private readonly workItemsService = inject(WorkItemsFacadeService);
  private readonly clientsService = inject(ClientFacadeService);
  private readonly reportsService = inject(ReportsService);
  private readonly workspaceService = inject(WorkspacesService);

  readonly activeWorkspaceId = computed(() => this.authStore.defaultWorkspace()?.id!);
  readonly currentUserId = computed(() => this.authStore.user()?.id);
  readonly isOwner = this.authStore.isOwner;

  readonly loading = signal(false);
  readonly loadingData = signal(false);
  readonly start = signal<Date | null>(DateTimeUtils.getWeekRange().start);
  readonly end = signal<Date | null>(DateTimeUtils.getWeekRange().end);
  readonly range = signal<Date[] | null>(null);
  readonly reportData = signal<SummarizeReportResponseDto | null>(null);
  readonly detailedReportData = signal<DetailedReportResponseDto | null>(null);
  readonly selectedView = signal<ReportView>('summary');
  reportRequestDto = signal<ReportRequestDto | null>(null);

  isFilterButtonDisabled = signal<boolean>(false);

  readonly workItems = signal<WorkItemDto[]>([]);
  readonly clients = signal<ClientDto[]>([]);
  readonly users = signal<WorkspaceUserDto[]>([]);
  usersValue = computed(() =>
    this.users().map((u) => ({
      ...u,
      fullName: `${u.firstName} ${u.lastName}`,
    })),
  );

  workItemsValue: WorkItemDto[] = [];
  clientsValue: ClientDto[] = [];
  private lastHandledWorkItemsRefresh = 0;

  readonly dateRange = computed(() => {
    const start = this.start();
    const end = this.end();
    return start && end ? { start, end } : null;
  });

  readonly filterForm = new FormGroup(
    {
      dateRangeFrom: new FormControl<Date | null>(null, Validators.required),
      dateRangeTo: new FormControl<Date | null>(null, Validators.required),
      clients: new FormControl<string[]>([]),
      users: new FormControl<string[]>([]),
      workItems: new FormControl<string[]>([]),
      billable: new FormControl<Array<string> | null>(null),
    },
    { validators: [atLeastOneSelectedValidator] },
  );

  readonly stateOptions = [
    { label: 'Ogólny', value: 'summary' as ReportView },
    { label: 'Szczegółowy', value: 'detailed' as ReportView },
  ];

  readonly statusOptions = [
    {
      label: 'Płatne',
      value: 'billable',
    },
    {
      label: 'Niepłatne',
      value: 'nonBillable',
    },
  ];

  constructor() {
    this.setupDataLoading();
    this.setupDateRangeSync();
    this.setupAutoFetch();
    this.setupExternalRefresh();
  }

  private mapStatus(billable: string[] | null): boolean | undefined {
    if (!billable?.length) return undefined;

    const hasB = billable.includes('billable');
    const hasNon = billable.includes('nonBillable');

    if (hasB && hasNon) return undefined;
    if (hasB) return true;
    if (hasNon) return false;

    return undefined;
  }

  ngOnInit(): void {
    this.setupRouteHandling();
    this.setupQueryParamsHandling();
  }

  private setupDataLoading(): void {
    effect(() => {
      const workspaceId = this.activeWorkspaceId();
      const userId = this.currentUserId();

      if (workspaceId && userId) {
        this.workItemsService
          .getAll(workspaceId)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe((items) => {
            this.workItemsValue = items;
            this.workItems.set(items);
          });

        this.clientsService
          .getClients(workspaceId)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe((page) => {
            this.clientsValue = page.content ?? [];
            this.clients.set(this.clientsValue);
          });

        if (this.isOwner()) {
          this.workspaceService
            .getWorkspaceUsers(workspaceId)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((page) => {
              this.users.set(page.content ?? []);
            });
        }
      }
    });
  }

  private setupDateRangeSync(): void {
    effect(() => {
      const range = this.dateRange();
      if (!range) return;

      this.filterForm.patchValue(
        {
          dateRangeFrom: range.start,
          dateRangeTo: range.end,
        },
        { emitEvent: false },
      );
    });
  }

  private setupAutoFetch(): void {
    toObservable(this.dateRange)
      .pipe(
        filter((range): range is DateRange => range !== null),
        debounceTime(300),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => this.fetchData());
  }

  private setupExternalRefresh(): void {
    effect(() => {
      const refreshToken = this.workItemsService.refresh();
      const range = this.dateRange();

      if (!range || refreshToken === this.lastHandledWorkItemsRefresh) {
        return;
      }

      this.lastHandledWorkItemsRefresh = refreshToken;
      this.fetchData();
    });
  }

  private setupRouteHandling(): void {
    this.route.url.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((urlSegments) => {
      const lastSegment = urlSegments[urlSegments.length - 1]?.path;
      this.selectedView.set(lastSegment === 'detailed' ? 'detailed' : 'summary');
    });
  }

  private setupQueryParamsHandling(): void {
    this.route.queryParamMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params) => {
      const startParam = params.get('start');
      const endParam = params.get('end');

      const weekUTC =
        startParam && endParam
          ? { start: new Date(startParam), end: new Date(endParam) }
          : this.getCurrentWeekUTC();

      if (!startParam || !endParam) {
        this.updateUrl(weekUTC);
      }

      this.setWeek(weekUTC);
    });
  }

  fetchData(): void {
    const workspaceId = this.activeWorkspaceId();
    const start = this.start();
    const end = this.end();

    if (!workspaceId || !start || !end) return;

    this.loadingData.set(true);
    this.reportRequestDto.set(this.prepareReportRequestDto(start, end));
    const requestDto = this.prepareReportRequestDto(start, end);

    if (this.selectedView() === 'summary') {
      this.reportsService
        .getSummarizeReport(workspaceId, requestDto)
        .pipe(tap(() => this.isFilterButtonDisabled.set(true)))
        .subscribe({
          next: (response) => {
            this.reportData.set(response);
            this.loadingData.set(false);
          },
          error: () => this.loadingData.set(false),
        });
    } else if (this.selectedView() === 'detailed') {
      this.reportsService
        .getDetailedReport(workspaceId, requestDto)
        .pipe(tap(() => this.isFilterButtonDisabled.set(true)))
        .subscribe({
          next: (response) => {
            this.detailedReportData.set(response);
            this.loadingData.set(false);
          },
          error: () => this.loadingData.set(false),
        });
    }
  }

  private prepareReportRequestDto(start: Date, end: Date) {
    const requestDto: ReportRequestDto = {
      startDate: start.toISOString(),
      endDate: end.toISOString(),
      clientsIds: this.filterForm.get('clients')!.value ?? [],
      workItemsIds: this.filterForm.get('workItems')!.value ?? [],
      usersIds: this.filterForm.get('users')!.value ?? [],
      billable: this.mapStatus(this.filterForm.value.billable!),
      interval: 'DAY',
    };
    return requestDto;
  }

  onRangeChange(range: DateRangeSelection): void {
    this.isFilterButtonDisabled.set(false);
    this.setWeek(range);
    this.updateUrl(range);
  }

  goToPreviousWeek(): void {
    this.shiftWeek(-7);
  }

  goToNextWeek(): void {
    this.shiftWeek(7);
  }

  private shiftWeek(days: number): void {
    const start = this.start();
    if (!start) return;

    const target = new Date(start);
    target.setUTCDate(target.getUTCDate() + days);

    const week = DateTimeUtils.getWeekUTCFromDate(target);
    this.setWeek(week);
    this.updateUrl(week);
  }

  private setWeek(week: DateRange): void {
    this.start.set(week.start);
    this.end.set(week.end);
    this.range.set([DateTimeUtils.utcToLocal(week.start), DateTimeUtils.utcToLocal(week.end)]);
  }

  private updateUrl(week: DateRange): void {
    this.router.navigate([], {
      queryParams: {
        start: week.start.toISOString(),
        end: week.end.toISOString(),
      },
      replaceUrl: true,
    });
  }

  private getCurrentWeekUTC(): DateRange {
    const now = new Date();
    const utcNow = DateTimeUtils.localToUTC(now);
    return DateTimeUtils.getWeekUTCFromDate(utcNow);
  }

  applyFilter(): void {
    this.fetchData();
  }

  onViewChange(value: ReportView): void {
    this.router.navigate(['/reports', value]);
  }

  protected onClientChange($event: MultiSelectChangeEvent) {
    this.isFilterButtonDisabled.set(false);
    this.filterForm.patchValue({ workItems: [] }, { emitEvent: false });

    const selectedClientIds = $event.value as string[];

    if (selectedClientIds.length === 0) {
      this.workItems.set(this.workItemsValue);
      return;
    }

    const filteredWorkItems = this.workItemsValue.filter(
      (item) => item.clientId && selectedClientIds.includes(item.clientId),
    );

    this.workItems.set(filteredWorkItems);
  }

  protected onWorkItemChange($event: MultiSelectChangeEvent) {
    this.isFilterButtonDisabled.set(false);

    const selectedWorkItemIds = $event.value as string[];

    if (selectedWorkItemIds.length === 0) {
      this.clients.set(this.clientsValue);
      return;
    }

    const selectedWorkItems = this.workItemsValue.filter((item) =>
      selectedWorkItemIds.includes(item.id!),
    );

    const clientIdsArray = selectedWorkItems.map((item) => item.clientId);
    const clientIds = new Set(clientIdsArray.filter(Boolean));
    const filteredClients = this.clientsValue.filter((client) => clientIds.has(client.id));

    this.clients.set(filteredClients);
  }
}
